// export.js — CSV generation and browser download for projection results

/**
 * Generate a CSV string from projection results for a given scenario.
 * @param {Object} result - full runProjection result
 * @param {string} scenarioKey - 'base', 'optimistic', 'adverse', etc.
 * @returns {string} CSV content
 */
export function generateCSV(result, scenarioKey = 'base') {
  const scenario = result.scenarios[scenarioKey];
  if (!scenario) return '';

  const { meta, benchmark, assumptions } = result;
  const lines = [];

  // Header section with assumptions
  lines.push('# Projection Export');
  lines.push(`# Scenario: ${scenarioKey}`);
  lines.push(`# Base Currency: ${meta.baseCurrency}`);
  lines.push(`# Hedge Ratio: ${(meta.hedgeRatio * 100).toFixed(0)}%`);
  lines.push(`# Mode: ${meta.mode}`);
  lines.push(`# Reinvestment Tenor: ${meta.tenorDays} days`);
  lines.push('#');
  lines.push('# Assumptions:');
  assumptions.forEach(a => {
    lines.push(`# ${a}`);
  });
  lines.push('#');
  lines.push('');

  // Data header
  lines.push([
    'Year',
    'Value (Hedged)',
    'Value (Unhedged)',
    'Income',
    'Withdrawals',
    'Fees',
    'Taxes',
    'Foreign Exchange Impact',
    'Forward Cost',
    'Benchmark Value',
    'Alpha (Hedged vs Benchmark)',
  ].join(','));

  // Data rows
  scenario.years.forEach((y, idx) => {
    const bm = benchmark.years[idx] || {};
    const alpha = (y.valueHedged || 0) - (bm.value || 0);
    lines.push([
      y.year,
      (y.valueHedged || 0).toFixed(2),
      (y.valueUnhedged || 0).toFixed(2),
      (y.income || 0).toFixed(2),
      (y.withdrawal || 0).toFixed(2),
      (y.fees || 0).toFixed(2),
      (y.taxes || 0).toFixed(2),
      (y.fxImpact || 0).toFixed(2),
      (y.forwardCost || 0).toFixed(2),
      (bm.value || 0).toFixed(2),
      alpha.toFixed(2),
    ].join(','));
  });

  return lines.join('\n');
}

/**
 * Trigger a browser download of content as a file.
 * @param {string} content - file content
 * @param {string} filename - download filename
 * @param {string} mimeType - MIME type
 */
export function downloadBlob(content, filename, mimeType = 'text/csv') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
